import React, { useContext, useEffect } from 'react';
import { DataContext } from '../context/DataProvider';
import { ExternalLink, ArrowLeft } from 'lucide-react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';

const Projects = () => {
    const { projects, loading } = useContext(DataContext);

    // Scroll to top on mount
    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    return (
        <div className="bg-white min-h-screen pt-32 pb-20">
            <Helmet>
                <title>Our Portfolio & Case Studies | Web Development & App Projects | PyrusMedia</title>
                <meta name="description" content="Explore PyrusMedia's portfolio of custom web application development, mobile app development, SaaS engineering, and AI automation projects in Pune." />
                <meta name="keywords" content="custom web development portfolio, mobile app development company Pune case studies, custom digital solutions, PyrusMedia projects" />
            </Helmet>

            <div className="container mx-auto px-4">
                {/* Header */}
                <div className="mb-12">
                     <Link to="/" className="inline-flex items-center gap-2 text-gray-500 hover:text-gray-900 mb-6 transition-colors">
                        <ArrowLeft size={20} /> Back to Home
                    </Link>
                    <h1 className="text-4xl md:text-6xl font-bold text-gray-900 mb-6">Our Work</h1>
                    <p className="text-xl text-gray-500 max-w-2xl">
                        A showcase of our recent projects. We build scalable, high-performance digital solutions tailored to your business needs.
                    </p>
                </div>

                {/* Projects Grid */}
                {loading ? (
                    <div className="text-center py-20">
                        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#bebd19] mx-auto"></div>
                    </div>
                ) : projects.length > 0 ? (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                        {projects.map((project) => (
                            <div key={project.id} className="group relative rounded-2xl overflow-hidden bg-gray-100 border border-gray-200 hover:border-[#bebd19] transition-all duration-500 flex flex-col h-full">
                                {/* Image Container */}
                                <div className="h-64 overflow-hidden relative">
                                    <div className="absolute inset-0 bg-black/5 group-hover:bg-black/0 transition-colors z-10" />
                                    <img
                                        src={project.image_url || 'https://images.unsplash.com/photo-1551288049-bebda4e38f71?auto=format&fit=crop&q=80&w=800'}
                                        alt={project.title}
                                        className="w-full h-full object-cover transform group-hover:scale-110 transition-transform duration-700"
                                    />
                                </div>

                                {/* Content */}
                                <div className="p-6 flex flex-col flex-grow">
                                    <span className="text-xs font-bold text-[#bebd19] uppercase tracking-wider mb-2 block">
                                        {project.category}
                                    </span>
                                    <h3 className="text-2xl font-bold text-gray-900 mb-3 group-hover:text-[#bebd19] transition-colors">
                                        {project.title}
                                    </h3>
                                    <p className="text-gray-500 text-sm mb-6 flex-grow">
                                        {project.description}
                                    </p>
                                    
                                    {project.project_link ? (
                                        <a 
                                            href={project.project_link} 
                                            target="_blank" 
                                            rel="noopener noreferrer" 
                                            className="inline-flex items-center gap-2 text-gray-900 font-semibold hover:text-[#bebd19] transition-colors mt-auto"
                                        >
                                            View Project <ExternalLink size={16} />
                                        </a>
                                    ) : (
                                        <span className="text-gray-600 text-sm italic mt-auto">Internal Project</span>
                                    )}
                                </div>
                            </div>
                        ))}
                    </div>
                ) : (
                    <div className="text-center py-20 bg-gray-100 rounded-2xl border border-gray-200 border-dashed">
                        <p className="text-gray-500 text-lg">No projects to display yet. Check back soon!</p>
                    </div>
                )}
            </div>
        </div>
    );
};

export default Projects;
